/**
 * Prosty markdown→HTML renderer (bez zewnetrznych libów).
 * Obsługuje: # ## ### headers, **bold**, `code`, ```code blocks```,
 * [links](url), * - listy, > blockquote, tabele (| col |), <hr>.
 */
import { useMemo } from "react";
import DOMPurify from "dompurify";

function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function mdToHtml(md) {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inCodeBlock = false;
  let codeLang = "";
  let codeBuf = [];
  let inTable = false;
  let tableHeaderDone = false;
  let inList = false;
  let listType = ""; // ul | ol

  const flushList = () => {
    if (inList) {
      out.push(`</${listType}>`);
      inList = false;
      listType = "";
    }
  };
  const flushTable = () => {
    if (inTable) {
      out.push("</tbody></table>");
      inTable = false;
      tableHeaderDone = false;
    }
  };

  const inlineFmt = (s) => {
    // Escapuj HTML najpierw
    s = escapeHtml(s);
    // inline code
    s = s.replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1.5 py-0.5 rounded text-sm">$1</code>');
    // bold
    s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    // italic
    s = s.replace(/(^|\s)\*([^*]+)\*(?=\s|$)/g, "$1<em>$2</em>");
    // links [text](url) — z target/rel
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener">$1</a>');
    // auto-link na url
    s = s.replace(/(\s|^)(https?:\/\/[^\s<]+)/g,
      '$1<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener">$2</a>');
    return s;
  };

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Code blocks
    if (line.trim().startsWith("```")) {
      if (!inCodeBlock) {
        flushList(); flushTable();
        inCodeBlock = true;
        codeLang = line.trim().slice(3);
        codeBuf = [];
      } else {
        out.push(`<pre class="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4 text-sm"><code data-lang="${codeLang}">${codeBuf.map(escapeHtml).join("\n")}</code></pre>`);
        inCodeBlock = false;
        codeLang = "";
        codeBuf = [];
      }
      continue;
    }
    if (inCodeBlock) { codeBuf.push(line); continue; }

    // Tables (| col1 | col2 |)
    if (line.trim().startsWith("|") && line.trim().endsWith("|")) {
      const cells = line.split("|").slice(1, -1).map(c => c.trim());
      if (!inTable) {
        flushList();
        out.push('<table class="border-collapse my-6 w-full"><thead>');
        out.push("<tr>" + cells.map(c => `<th class="border border-slate-300 bg-slate-100 px-3 py-2 text-left">${inlineFmt(c)}</th>`).join("") + "</tr>");
        out.push('</thead><tbody>');
        inTable = true;
        tableHeaderDone = false;
        continue;
      }
      // Separator row (|---|---|)
      if (!tableHeaderDone && cells.every(c => /^-+:?$/.test(c) || /^:-+$/.test(c) || /^:-+:$/.test(c))) {
        tableHeaderDone = true;
        continue;
      }
      out.push("<tr>" + cells.map(c => `<td class="border border-slate-300 px-3 py-2 align-top">${inlineFmt(c)}</td>`).join("") + "</tr>");
      continue;
    } else if (inTable) {
      flushTable();
    }

    // Headers
    if (/^###\s/.test(line)) {
      flushList();
      const text = line.replace(/^###\s+/, "");
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      out.push(`<h3 id="${id}" class="text-xl font-bold mt-8 mb-3 text-slate-800">${inlineFmt(text)}</h3>`);
      continue;
    }
    if (/^##\s/.test(line)) {
      flushList();
      const text = line.replace(/^##\s+/, "");
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      out.push(`<h2 id="${id}" class="text-2xl font-bold mt-10 mb-4 pb-2 border-b border-slate-200 text-slate-900">${inlineFmt(text)}</h2>`);
      continue;
    }
    if (/^#\s/.test(line)) {
      flushList();
      const text = line.replace(/^#\s+/, "");
      out.push(`<h1 class="text-3xl font-bold mt-8 mb-4 text-slate-900">${inlineFmt(text)}</h1>`);
      continue;
    }

    // Anchor target (np. <a id="szybki-start"></a>)
    if (/^<a id="[^"]+"><\/a>/.test(line.trim())) {
      out.push(line.trim());
      continue;
    }

    // HR
    if (/^---+\s*$/.test(line)) {
      flushList();
      out.push('<hr class="my-8 border-slate-200">');
      continue;
    }

    // Blockquote
    if (line.startsWith("> ")) {
      flushList();
      out.push(`<blockquote class="border-l-4 border-blue-500 bg-blue-50 pl-4 py-2 my-4 italic text-slate-700">${inlineFmt(line.slice(2))}</blockquote>`);
      continue;
    }

    // Lists
    const ulMatch = line.match(/^\s*[-*+]\s+(.+)/);
    const olMatch = line.match(/^\s*(\d+)\.\s+(.+)/);
    if (ulMatch) {
      if (!inList || listType !== "ul") {
        flushList();
        out.push('<ul class="list-disc pl-6 my-3 space-y-1">');
        inList = true;
        listType = "ul";
      }
      out.push(`<li>${inlineFmt(ulMatch[1])}</li>`);
      continue;
    }
    if (olMatch) {
      if (!inList || listType !== "ol") {
        flushList();
        out.push('<ol class="list-decimal pl-6 my-3 space-y-1">');
        inList = true;
        listType = "ol";
      }
      out.push(`<li>${inlineFmt(olMatch[2])}</li>`);
      continue;
    }
    if (inList) flushList();

    // Empty line / paragraph
    if (!line.trim()) {
      continue;
    }

    out.push(`<p class="my-3 text-slate-700 leading-relaxed">${inlineFmt(line)}</p>`);
  }
  flushList();
  flushTable();

  return out.join("\n");
}

export function SimpleMarkdown({ source, className = "" }) {
  const html = useMemo(() => {
    const raw = mdToHtml(source || "");
    return DOMPurify.sanitize(raw, {
      ADD_ATTR: ["id", "target", "rel", "data-lang"],
      USE_PROFILES: { html: true },
    });
  }, [source]);

  return (
    <div
      className={`prose prose-slate max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default SimpleMarkdown;
